import React, { useState, useEffect } from "react";
import { CreateRegistryItemData, registryAPI } from "../api/registry";
import { RegistryItem } from "../lib/supabase";

interface AddRegistryItemFormProps {
  onSubmit: (data: CreateRegistryItemData) => Promise<void>;
  onCancel: () => void;
  item?: RegistryItem; // Optional item for editing
}

interface FormField {
  key: keyof CreateRegistryItemData;
  label: string;
  type: "text" | "number" | "url" | "textarea" | "select";
  required: boolean;
  placeholder?: string;
  step?: string;
  rows?: number;
  options?: { value: string; label: string }[];
}

const formFields: FormField[] = [
  {
    key: "type",
    label: "Item Type",
    type: "select",
    required: true,
    options: [
      { value: "item", label: "Regular Item" },
      { value: "external_link", label: "External Registry Link" },
    ],
  },
  {
    key: "name",
    label: "Item Name",
    type: "text",
    required: true,
    placeholder: "Enter the item name...",
  },
  {
    key: "price",
    label: "Price",
    type: "number",
    required: false, // Will be conditionally required based on type
    placeholder: "0.00",
    step: "0.01",
  },
  {
    key: "description",
    label: "Description",
    type: "textarea",
    required: false, // Will be conditionally required based on type
    placeholder: "Describe the item...",
    rows: 3,
  },
  {
    key: "image_url",
    label: "Image URL",
    type: "url",
    required: true, // Always required
    placeholder: "https://example.com/image.jpg",
  },
  {
    key: "registry_url",
    label: "Registry URL",
    type: "url",
    required: true, // Always required
    placeholder: "https://store.com/item",
  },
  {
    key: "category",
    label: "Category",
    type: "text",
    required: false, // Will be conditionally required based on type
    placeholder: "Kitchen, Bedroom, etc.",
  },
];

const AddRegistryItemForm: React.FC<AddRegistryItemFormProps> = ({
  onSubmit,
  onCancel,
  item,
}) => {
  const [formData, setFormData] = useState<Record<string, string>>(
    formFields.reduce((acc, field) => ({ ...acc, [field.key]: "" }), {})
  );
  const [isScraping, setIsScraping] = useState(false);
  const [scrapingError, setScrapingError] = useState<string | null>(null);

  // Initialize form with item data if editing
  useEffect(() => {
    if (item) {
      setFormData({
        type: item.type,
        name: item.name,
        price: item.price.toString(),
        description: item.description,
        image_url: item.image_url || "",
        registry_url: item.registry_url,
        category: item.category,
      });
    }
  }, [item]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields based on type
    const isExternalLink = formData.type === "external_link";

    // Always required fields
    if (
      !formData.type ||
      !formData.name ||
      !formData.image_url ||
      !formData.registry_url
    ) {
      alert(
        "Please fill in all required fields: Item Type, Item Name, Image URL, and Registry URL."
      );
      return;
    }

    // Additional required fields for regular items
    if (
      !isExternalLink &&
      (!formData.price || !formData.description || !formData.category)
    ) {
      alert(
        "For regular items, please also fill in: Price, Description, and Category."
      );
      return;
    }

    try {
      const submitData = {
        ...formData,
        price: isExternalLink ? 0 : parseFloat(formData.price),
        description: isExternalLink ? "" : formData.description,
        category: isExternalLink ? "External Registry" : formData.category,
      } as CreateRegistryItemData;

      await onSubmit(submitData);

      // Reset form
      setFormData(
        formFields.reduce((acc, field) => ({ ...acc, [field.key]: "" }), {})
      );
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  const handleInputChange =
    (field: string) =>
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >
    ) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleUrlChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setFormData((prev) => ({ ...prev, registry_url: url }));

    // Clear previous scraping error
    setScrapingError(null);

    // Disable scraping for external link items
    if (formData.type === "external_link") {
      return;
    }

    // Only attempt to scrape if URL looks valid and is not empty
    if (url && url.startsWith("http") && url.length > 10) {
      setIsScraping(true);
      try {
        const result = await registryAPI.scrapeProduct(url);

        if (result.success && result.data) {
          // Auto-fill form with scraped data, only overwriting empty fields
          const scrapedData = result.data;
          setFormData((prev) => ({
            ...prev,
            name: prev.name || scrapedData.name || "",
            price:
              prev.price ||
              (scrapedData.price ? scrapedData.price.toString() : ""),
            description: prev.description || scrapedData.description || "",
            image_url: prev.image_url || scrapedData.image_url || "",
            category: prev.category || scrapedData.category || "",
          }));
        } else {
          setScrapingError(
            result.error || "Failed to scrape product information"
          );
        }
      } catch (error) {
        console.error("Scraping error:", error);
        setScrapingError("Failed to scrape product information");
      } finally {
        setIsScraping(false);
      }
    }
  };

  const renderField = (field: FormField) => {
    // Determine if field is required based on current form data
    const isRequired =
      field.key === "type" ||
      field.key === "name" ||
      field.key === "image_url" ||
      field.key === "registry_url" ||
      (formData.type === "item" &&
        (field.key === "price" ||
          field.key === "description" ||
          field.key === "category"));

    const commonProps = {
      id: field.key,
      value: formData[field.key],
      required: isRequired,
      placeholder: field.placeholder,
      className: "form-input",
    };

    if (field.type === "textarea") {
      return (
        <textarea
          {...commonProps}
          rows={field.rows || 3}
          onChange={handleInputChange(field.key)}
        />
      );
    }

    if (field.type === "select") {
      return (
        <select {...commonProps} onChange={handleInputChange(field.key)}>
          <option value="">Select {field.label.toLowerCase()}...</option>
          {field.options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        {...commonProps}
        type={field.type}
        step={field.step}
        onChange={
          field.key === "registry_url"
            ? handleUrlChange
            : handleInputChange(field.key)
        }
      />
    );
  };

  const renderFieldGroup = (fields: FormField[]) => {
    if (fields.length === 1) {
      const field = fields[0];
      const isRequired =
        field.key === "type" ||
        field.key === "name" ||
        field.key === "image_url" ||
        field.key === "registry_url" ||
        (formData.type === "item" &&
          (field.key === "price" ||
            field.key === "description" ||
            field.key === "category"));

      return (
        <div key={field.key} className="form-group">
          <label htmlFor={field.key}>
            {field.label}
            {isRequired && <span className="required"> *</span>}
          </label>
          {renderField(field)}
          {field.key === "registry_url" && (
            <div className="url-help">
              {isScraping && (
                <span className="scraping-indicator">
                  🔄 Scraping and cleaning product information...
                </span>
              )}
              {scrapingError && (
                <span className="scraping-error">⚠️ {scrapingError}</span>
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <div key={fields.map((f) => f.key).join("-")} className="form-row">
        {fields.map((field) => {
          const isRequired =
            field.key === "type" ||
            field.key === "name" ||
            field.key === "image_url" ||
            field.key === "registry_url" ||
            (formData.type === "item" &&
              (field.key === "price" ||
                field.key === "description" ||
                field.key === "category"));

          return (
            <div key={field.key} className="form-group">
              <label htmlFor={field.key}>
                {field.label}
                {isRequired && <span className="required"> *</span>}
              </label>
              {renderField(field)}
              {field.key === "registry_url" && (
                <div className="url-help">
                  {isScraping && (
                    <span className="scraping-indicator">
                      🔄 Scraping and cleaning product information...
                    </span>
                  )}
                  {scrapingError && (
                    <span className="scraping-error">⚠️ {scrapingError}</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Group fields for layout - move registry_url to the top for better UX
  const fieldGroups = [
    [formFields[0]], // type
    [formFields[5]], // registry_url
    [formFields[1], formFields[2]], // name, price
    [formFields[3]], // description
    [formFields[4]], // image_url
    [formFields[6]], // category
  ];

  return (
    <form onSubmit={handleSubmit} className="add-item-form">
      <h3>{item ? "Edit Registry Item" : "Add New Registry Item"}</h3>

      <div className="form-help">
        <p>
          💡 <strong>Pro tip:</strong> Choose "External Registry Link" for items
          on other registry sites (like Target, Amazon, etc.) or "Regular Item"
          for items you want to track directly.
        </p>
        <p>
          <strong>External Links:</strong> Only require Name, Image URL, and
          Registry URL.
          <br />
          <strong>Regular Items:</strong> Require all fields including Price,
          Description, and Category.
        </p>
      </div>

      {fieldGroups.map(renderFieldGroup)}

      <div className="form-actions">
        <button type="submit" className="submit-btn" disabled={isScraping}>
          {isScraping ? "Scraping..." : item ? "Update Item" : "Add Item"}
        </button>
        <button type="button" onClick={onCancel} className="cancel-btn">
          Cancel
        </button>
      </div>
    </form>
  );
};

export default AddRegistryItemForm;
